import ELK, { type ElkNode } from "elkjs/lib/elk.bundled.js";
import sortBy from "lodash/sortBy";
import type { Edge, Node } from "reactflow";

import {
  BASE_NODE_HEIGHT,
  JOURNEY_BRANCH_NODES,
  MESSAGE_NODE_HEIGHT,
  NODE_DETAIL_HEIGHT,
  MINI_NODE_MIN_HEIGHT,
  NODE_SIBLING_SPACING,
  NODE_SPACING,
  NODE_WIDTH,
  VISIBLE_SYNC_COUNT,
  NODE_COMPACT_SPACING,
  MAX_VISIBLE_NOTES_ON_NODE,
} from "src/pages/journeys/constants";
import { bodyAbTestArms } from "src/pages/journeys/nodes/node-ab-test-summary";
import { type JourneyNodeConfig, JourneyNodeType } from "src/types/journeys";
import type {
  ExecutableJourneyNode,
  JourneyNodeDetails,
  JourneyNoteDetails,
} from "src/pages/journeys/types";
import { getOutgoersSafe } from "src/pages/journeys/utils/graph-helpers";
import { getDownstreamNodes } from "src/pages/journeys/utils/get-downstream-nodes";
import { NOTE_ATTACHED_MIN_HEIGHT } from "src/pages/journeys/constants";

const getSizeOfNodeByType = (node: JourneyNodeDetails<JourneyNodeConfig>) => {
  switch (node.config.type) {
    case JourneyNodeType.Sync:
      return (
        BASE_NODE_HEIGHT +
        (node.sync_configs?.slice(0, VISIBLE_SYNC_COUNT).length ?? 0) * 25
      );
    case JourneyNodeType.Decision:
      return BASE_NODE_HEIGHT + 2 * NODE_DETAIL_HEIGHT;
    case JourneyNodeType.EntryCohort:
    case JourneyNodeType.WaitUntilEvent:
    case JourneyNodeType.Function:
      // Two rows of details
      return BASE_NODE_HEIGHT + 2 * NODE_DETAIL_HEIGHT;
    case JourneyNodeType.EventEmission:
      return BASE_NODE_HEIGHT + NODE_DETAIL_HEIGHT;
    case JourneyNodeType.EntryEvent:
      return node.config.max_num_entries === -1
        ? BASE_NODE_HEIGHT + 3 * NODE_DETAIL_HEIGHT
        : BASE_NODE_HEIGHT + 2 * NODE_DETAIL_HEIGHT;
    case JourneyNodeType.EntryAudienceExperiment: {
      // Audience row + re-entry row, plus an arms block (header/snapshot +
      // one row per split group) when arms are configured.
      const numArms = node.config.splitGroups?.length ?? 0;
      return (
        BASE_NODE_HEIGHT +
        2 * NODE_DETAIL_HEIGHT +
        (numArms > 0 ? NODE_DETAIL_HEIGHT + numArms * 36 : 0)
      );
    }
    case JourneyNodeType.Message:
      // A body A/B test empties the base row and renders arm previews instead,
      // so the tile is just as tall with nothing on `rendered_output`.
      return node.message_config?.rendered_output ||
        bodyAbTestArms(node.message_config?.experiments).length > 0
        ? MESSAGE_NODE_HEIGHT
        : BASE_NODE_HEIGHT;
    default:
      return BASE_NODE_HEIGHT;
  }
};

// Compute extra height contributed by notes attached to a node.
// We render up to MAX_VISIBLE_NOTES_ON_NODE visible notes within the tile; each note has a minimum height.
export const getAdditionalHeightFromNotes = (
  nodeId: string,
  notes: Array<{ node_id?: string | null }> | undefined,
) => {
  if (!notes || notes.length === 0) return 0;
  const attachedCount = notes.filter((n) => n.node_id === nodeId).length;
  if (attachedCount <= 0) return 0;
  const visibleCount = Math.min(MAX_VISIBLE_NOTES_ON_NODE, attachedCount);
  return visibleCount * NOTE_ATTACHED_MIN_HEIGHT;
};

export const reposition = async (
  nodes: Node[],
  edges: Edge[],
  isCompactView: boolean = false,
  notes?: JourneyNoteDetails[],
): Promise<Node[]> => {
  const elk = new ELK();

  const sortedNodes = sortNodesByTypeAndPriority(nodes);

  const elkEdges = edges.map((edge) => {
    return {
      id: edge.id,
      sources: [edge.source],
      targets: [edge.target],
    };
  });

  const graph: ElkNode = {
    id: "root",
    // https://www.eclipse.org/elk/reference/algorithms/org-eclipse-elk-layered.html
    layoutOptions: {
      "elk.layered.nodePlacement.bk.fixedAlignment": "RIGHTDOWN",
      "elk.algorithm": "layered",
      "elk.direction": "DOWN",
      "elk.layered.spacing.nodeNodeBetweenLayers": isCompactView
        ? NODE_COMPACT_SPACING.toString()
        : NODE_SPACING.toString(),
      "elk.layered.spacing.nodeNode": NODE_SIBLING_SPACING.toString(),
      "elk.layered.layering.strategy": "INTERACTIVE",
      "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
      "elk.layered.nodePlacement.strategy": "LINEAR_SEGMENTS",
      "elk.spacing.componentComponent": "50",
      "elk.spacing.nodeNode": NODE_SIBLING_SPACING.toString(),
    },
    children: sortNodesByTypeAndPriority(sortedNodes).map((node) => {
      const baseHeight = JOURNEY_BRANCH_NODES.includes(node.data.config.type)
        ? MINI_NODE_MIN_HEIGHT
        : isCompactView
          ? BASE_NODE_HEIGHT
          : (getSizeOfNodeByType(node.data) ??
            BASE_NODE_HEIGHT + 2 * NODE_DETAIL_HEIGHT);
      const extra = getAdditionalHeightFromNotes(node.id, notes);
      return {
        id: node.id,
        width: NODE_WIDTH,
        height: baseHeight + extra,
      };
    }),
    edges: elkEdges,
  };

  const layout = await elk.layout(graph);

  const repositionedNodes: Node[] = [];

  for (const node of sortedNodes) {
    const repositionedNode = layout.children?.find((n) => n.id === node.id);

    repositionedNodes.push({
      ...node,
      position: { x: repositionedNode?.x || 0, y: repositionedNode?.y || 0 },
    });
  }

  return alignBranchNodesWithOnlyChild(repositionedNodes, edges);
};

// ELK can offset a branch label from the tile it leads to, which kinks the connector between them.
// A tile fed only by its branch label has no other edge pulling it, so the label follows the tile.
export const alignBranchNodesWithOnlyChild = (
  nodes: Node[],
  edges: Edge[],
): Node[] => {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));

  return nodes.map((node) => {
    if (!JOURNEY_BRANCH_NODES.includes(node.data.config.type)) return node;

    const outgoing = edges.filter((edge) => edge.source === node.id);
    const child =
      outgoing.length === 1 ? nodesById.get(outgoing[0]!.target) : undefined;
    if (!child) return node;

    const childIncoming = edges.filter((edge) => edge.target === child.id);
    if (childIncoming.length !== 1) return node;

    return { ...node, position: { ...node.position, x: child.position.x } };
  });
};

export const needsJourneyRepositioning = (
  nodes: Array<Pick<Node, "position">>,
): boolean =>
  !nodes.every(
    (node) =>
      node.position && node.position.x != null && node.position.y != null,
  );

const sortNodesByTypeAndPriority = (nodes: Node[]): Node[] =>
  sortBy(nodes, [
    (n) => {
      switch (n.type) {
        case JourneyNodeType.EntryCohort:
        case JourneyNodeType.EntryEvent:
        case JourneyNodeType.EntryAudienceExperiment:
          return 1;
        case JourneyNodeType.EntryAudienceExperimentBranch:
          return 1.5;
        case JourneyNodeType.TimeDelay:
          return 2;
        case JourneyNodeType.WaitUntilEvent:
          return 3;
        case JourneyNodeType.WaitUntilEventBranch:
          return 4;
        case JourneyNodeType.Segments:
          return 5;
        case JourneyNodeType.SegmentBranch:
          return 6;
        case JourneyNodeType.Splits:
          return 7;
        case JourneyNodeType.SplitBranch:
          return 8;
        case JourneyNodeType.Sync:
          return 9;
        case JourneyNodeType.Function:
          return 10;
        default:
          return -1;
      }
    },
    "data.config.segment_priority_rank",
  ]);

/**
 * Centers a row of nodes horizontally based on an x coordinate
 * @param branchNodes - The nodes to center
 * @param centerX - The position of the parent node
 * @returns A map of node ids to their positions
 */
export const centerNodesOnX = (
  nodes: Node[],
  centerX: number,
): Map<string, { x: number; y: number }> => {
  const totalWidth = NODE_SPACING * 3 * (nodes.length - 1);
  const startX = centerX - totalWidth / 2;

  const positionMap = new Map<string, { x: number; y: number }>();

  nodes.forEach((node, index) => {
    positionMap.set(node.id, {
      x: startX + index * (NODE_SPACING * 3),
      y: node.position.y,
    });
  });

  return positionMap;
};

/** Center choices while moving each exclusive subtree as a unit. Shared joins stay put. */
export const layoutDecisionChoices = (
  sourceNodeId: string,
  nodes: ExecutableJourneyNode[],
  edges: Edge[],
  sourceHeight?: number,
): ExecutableJourneyNode[] => {
  const source = nodes.find((node) => node.id === sourceNodeId);
  if (!source || source.type !== JourneyNodeType.Decision) return nodes;
  const arms = getOutgoersSafe(source, nodes, edges);
  if (!arms.length) return nodes;

  const descendants = arms.map(
    (arm) => new Set(getDownstreamNodes(arm, nodes, edges)),
  );
  const subtrees = arms.map((arm, index) =>
    nodes.filter(
      (node) =>
        node.id !== source.id &&
        descendants[index]!.has(node.id) &&
        !descendants.some((ids, other) => other !== index && ids.has(node.id)),
    ),
  );
  // Don't rearrange graphs where one choice is itself downstream of another.
  if (subtrees.some((subtree, index) => !subtree.includes(arms[index]!)))
    return nodes;

  const width = (node: ExecutableJourneyNode) => node.width ?? NODE_WIDTH;
  const center = (node: ExecutableJourneyNode) =>
    node.position.x + width(node) / 2;
  const extents = subtrees.map((subtree, index) => ({
    left:
      center(arms[index]!) -
      Math.min(...subtree.map((node) => node.position.x)),
    right:
      Math.max(...subtree.map((node) => node.position.x + width(node))) -
      center(arms[index]!),
  }));
  const spacing = Math.max(
    NODE_WIDTH + NODE_SIBLING_SPACING,
    ...extents
      .slice(1)
      .map(
        (extent, index) =>
          extents[index]!.right + extent.left + NODE_SIBLING_SPACING,
      ),
  );
  const childY =
    source.position.y +
    (sourceHeight ?? source.height ?? getSizeOfNodeByType(source.data)) +
    NODE_SPACING * 1.5;
  const positions = new Map<string, { x: number; y: number }>();
  subtrees.forEach((subtree, index) => {
    const dx =
      center(source) +
      (index - (arms.length - 1) / 2) * spacing -
      center(arms[index]!);
    const dy = childY - arms[index]!.position.y;
    subtree.forEach((node) =>
      positions.set(node.id, {
        x: node.position.x + dx,
        y: node.position.y + dy,
      }),
    );
  });
  return nodes.map((node) =>
    positions.has(node.id)
      ? { ...node, position: positions.get(node.id)! }
      : node,
  );
};
